import { createEventBus } from '@solid-primitives/event-bus';

import { Events, type EventBus } from '~/constants';

type Listener<T extends Events> = (params: EventBus[T]) => void;

type BusItem<T extends Events> = {
  /** createEventBus 实例 */
  emmiter: ReturnType<typeof createEventBus<EventBus[T]>>;
  /** 监听的回调函数数量 */
  counter: Map<Listener<T>, VoidFunction>;
  /** 销毁事件监听 */
  dispose: VoidFunction;
};

class EvtBus {
  bus: Map<Events, BusItem<Events>>;

  constructor() {
    this.bus = new Map();
  }

  init<T extends Events>(evt: T, listener: Listener<T>): BusItem<T> {
    if (!this.bus.get(evt)) {
      const busItem = createRoot((dispose) => {
        const emmiter = createEventBus<EventBus[T]>();

        const unSubscribe = emmiter.listen(listener);

        const counter = new Map<Listener<T>, VoidFunction>();
        counter.set(listener, unSubscribe);

        return { emmiter, counter, dispose };
      });

      this.bus.set(evt, busItem as unknown as BusItem<Events>);
      return busItem;
    } else {
      const busItem = this.bus.get(evt)! as unknown as BusItem<T>;
      const unSubscribe = busItem.emmiter.listen(listener);
      busItem.counter.set(listener, unSubscribe);
      return busItem;
    }
  }

  dispose<T extends Events>(evt: T, listener: Listener<T> = () => null) {
    const busItem = this.bus.get(evt);
    const unSubscribe = busItem?.counter.get(listener as Listener<Events>);

    if (!unSubscribe) return;

    unSubscribe();
    busItem!.counter.delete(listener as Listener<Events>);

    if (busItem?.counter.size === 0) {
      busItem.dispose();
      this.bus.delete(evt);
    }
  }
}

const evtBus = new EvtBus();

/**
 * 在组件内使用，会自动销毁事件监听
 * @param evt
 * @param listener
 * @returns
 */
export const useEvtBus = <T extends Events>(evt: T, listener: Listener<T>) => {
  const { emmiter } = evtBus.init(evt, listener);

  onCleanup(() => {
    evtBus.dispose(evt, listener);
  });

  return (params: EventBus[T]) => emmiter.emit(params);
};

/**
 * 触发一个事件
 * @param evt
 * @param params
 */
export const emitEvtBus = <T extends Events>(
  /** 事件类型 */
  evt: T,
  /** 事件参数 */
  params?: EventBus[T]
) => {
  const busItem = evtBus.bus.get(evt);
  busItem?.emmiter.emit(params);
};

/**
 * 删除具体的某个事件监听
 * @param evt
 */
export const deleteEvtBus = (evt: Events) => {
  const busItem = evtBus.bus.get(evt);
  busItem?.dispose();
  evtBus.bus.delete(evt);
};

/**
 * 清空所有事件监听
 */
export const clearEvtBus = () => {
  Array.from(evtBus.bus.values()).forEach((busItem) => {
    busItem.dispose();
  });
  evtBus.bus.clear();
};

export { Events, EventBus };

export default evtBus;
